import { PopperProps } from '@mui/material'
import { GridRenderEditCellParams } from '@mui/x-data-grid-premium'
import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import SingleDropdownCell from 'src/components/DataGrid/cells/SingleDropdownCell/SingleDropdownCell'
import TextFieldCell from 'src/components/DataGrid/cells/TextFieldCell/TextFieldCell'
import { useExpenseCategoryDropdownOptions } from 'src/components/ExpenseCategoryDropdownV2/useExpenseCategoryDropdownOptions'
import { TransactionsDataGridRowModel } from 'src/components/TransactionsDataGrid/transactionsDataGrid.types'
import { useExtendLocation } from 'src/hooks/locationHooks'
import { useTranslate } from 'src/localization/Trns'
import { ExpenseCategoryResponse } from 'src/queries/organizations/expenseCategories.queries'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'

interface TransactionExpenseCategoryDropdownCellProps {
  params: GridRenderEditCellParams<TransactionsDataGridRowModel, string>
  editMode: 'row' | 'cell'
  category: ExpenseCategoryResponse
  loading: boolean
  disabled?: boolean
  dropdownContainer?: PopperProps['container']
}

const TransactionExpenseCategoryDropdownCell: React.FC<
  TransactionExpenseCategoryDropdownCellProps
> = (props): React.ReactElement => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const transactionId = props.params.row.transaction.id
  const extendLocation = useExtendLocation()

  const { optionGroups, infiniteScrollProps, suggestedOptionValues } =
    useExpenseCategoryDropdownOptions({
      expenseCategoryId: props.category.id,
      transactionId,
      freeTextAllowed: props.category.freeTextAllowed,
      additionalKey: extendLocation.matchedPath,
    })

  const expenseCategoryCellData =
    props.params.row.expenseCategoryCellData[`ec:${props.category.id}`]
  const shouldStartWithEmptyValue =
    expenseCategoryCellData?.showMissingWhenEmpty &&
    props.category.freeTextAllowed &&
    !props.params.value

  // override for case when exp cat is required & freeText & no labels & currently empty,
  // to prevent "Missing" being prefilled as formattedValue
  const modifiedParams = shouldStartWithEmptyValue
    ? {
        ...props.params,
        formattedValue: '',
      }
    : props.params

  const handleSelectSuccess = React.useCallback(
    (selectedValue: { value: string; label?: string }) => {
      const isSuggested = suggestedOptionValues.has(selectedValue.value)

      // Track analytics for expense category selection in list view
      analytics?.track(
        'transaction detail - edit transaction vendor and expense categories - button press',
        {
          intent: 'edit transaction vendor and expense categories',
          interaction: 'button press',
          screen: calculatedBackgroundLocation.pathname,
          source: 'list',
          expenseDetails: [
            {
              categoryId: props.category.id,
              labelId: selectedValue.value ?? '',
              suggested: isSuggested,
            },
          ],
        },
      )
    },
    [
      analytics,
      calculatedBackgroundLocation,
      props.category.id,
      suggestedOptionValues,
    ],
  )

  return props.category.freeTextAllowed && !props.category.hasLabels ? (
    <TextFieldCell
      params={modifiedParams}
      editMode={props.editMode}
      loadingState={props.loading ? 'pending' : 'idle'}
      disabled={props.disabled || props.loading}
    />
  ) : (
    <SingleDropdownCell
      optional
      optionGroups={optionGroups}
      params={modifiedParams}
      editMode={props.editMode}
      loadingState={props.loading ? 'pending' : 'idle'}
      disabled={props.disabled}
      searchable
      infiniteScrollProps={infiniteScrollProps}
      dropdownContainer={props.dropdownContainer}
      freeTextProps={{
        enabled: props.category.freeTextAllowed,
        existingOptionHeader: translate('Existing Labels'),
        freeTextOptionHeader: translate('New Label'),
      }}
      emptyMessage={{
        title: translate('No active labels'),
        subtitle: translate(
          'Looks like there are no active labels to choose from',
        ),
      }}
      onSelect={handleSelectSuccess}
    />
  )
}

export default TransactionExpenseCategoryDropdownCell
